/**
 * ASC 606 Phase 10e — Allocation Preview Panel
 * ============================================
 *
 * Renders the per-POB Step 4 allocation breakdown for a single contract:
 * resolved SSP (with source), specific discount (10c), net SSP,
 * proportional VC share, specific VC (10d), and final allocated_price.
 *
 * Embedded inside the RevenueReviews Step 4 section; also reusable
 * standalone on contract detail pages. Reads-only — displays the plan
 * that `POST /contracts/{id}/allocate` would commit.
 *
 * Backend: GET /api/v1/revenue/contracts/{id}/allocation-preview
 */

import { useEffect, useState } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';

const API = `${API_V1}/revenue`;

interface POBPlan {
  pob_id: number;
  po_number: number;
  description: string | null;
  resolved_ssp: number;
  ssp_source: string; // EXPLICIT | LIBRARY | RESIDUAL | MANUAL_OVERRIDE
  ssp_library_ref_id: number | null;
  discount_applied: number;
  net_ssp: number;
  vc_specific: number;
  vc_proportional_share: number;
  prior_allocated: number;
  new_allocated: number;
}

interface AllocationPreview {
  contract_id: number;
  contract_number: string;
  total_value: number;
  total_ssp: number;
  total_discount_specific: number;
  specific_discount_applied: boolean;
  proportional_vc_sum: number;
  specific_vc_sum: number;
  vc_applied: boolean;
  pob_count: number;
  pobs: POBPlan[];
}

interface Props {
  contractId: number;
  onError?: (msg: string) => void;
}

export default function AllocationPreviewPanel({ contractId, onError }: Props) {
  const [preview, setPreview] = useState<AllocationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    authFetch(`${API}/contracts/${contractId}/allocation-preview`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          const msg = body.detail || `HTTP ${r.status}`;
          throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
        return r.json();
      })
      .then((d: AllocationPreview) => {
        if (!cancelled) setPreview(d);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setErr(e.message);
          if (onError) onError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [contractId, onError]);

  if (loading) {
    return <div style={{ padding: 12, color: 'var(--text-secondary)', fontSize: 12 }}>Loading allocation preview…</div>;
  }
  if (err) {
    return (
      <div style={{
        padding: 12, background: 'var(--bg-surface)',
        border: '1px solid var(--border-strong)', borderRadius: 6,
        color: 'var(--color-negative)', fontSize: 12,
      }}>
        Allocation preview error: {err}
      </div>
    );
  }
  if (!preview) return null;

  const sumCheck = preview.pobs.reduce((s, p) => s + p.new_allocated, 0);
  const expected = preview.total_value + preview.proportional_vc_sum + preview.specific_vc_sum;
  const invariantOk = Math.abs(sumCheck - expected) < 0.02;

  return (
    <div style={{
      border: '1px solid var(--border-subtle)',
      borderRadius: 6,
      background: 'var(--bg-surface)',
      padding: 12,
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          Step 4 Allocation Preview · {preview.pob_count} POB{preview.pob_count === 1 ? '' : 's'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          Total {formatAmount(preview.total_value)}
          {preview.proportional_vc_sum !== 0 && <> · prop VC {formatAmount(preview.proportional_vc_sum)}</>}
          {preview.specific_vc_sum !== 0 && <> · spec VC {formatAmount(preview.specific_vc_sum)}</>}
          {preview.total_discount_specific > 0 && <> · disc {formatAmount(preview.total_discount_specific)}</>}
        </div>
      </div>

      {preview.pob_count === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: 8 }}>
          No performance obligations on this contract (MSA shell). Step 4 trivially satisfied.
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>POB</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>SSP</th>
              <th style={{ textAlign: 'center', padding: '6px 4px' }}>Source</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>Discount</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>Net SSP</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>Prop VC</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>Spec VC</th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 600 }}>Allocated</th>
            </tr>
          </thead>
          <tbody>
            {preview.pobs.map((p) => {
              const isResidual = (p.ssp_source || '').toUpperCase() === 'RESIDUAL';
              return (
                <tr key={p.pob_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '6px 4px', color: 'var(--text-primary)' }}>
                    #{p.po_number} · {p.description ? (p.description.length > 40 ? p.description.slice(0, 40) + '…' : p.description) : 'POB ' + p.pob_id}
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>{formatAmount(p.resolved_ssp)}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 10, fontSize: 10,
                      background: isResidual ? 'rgba(255, 180, 50, 0.15)' : 'var(--bg-card)',
                      color: isResidual ? 'var(--color-warning)' : 'var(--text-secondary)',
                      border: isResidual ? '1px solid var(--color-warning)' : '1px solid var(--border-subtle)',
                    }}>
                      {p.ssp_source}
                    </span>
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: p.discount_applied > 0 ? 'var(--color-negative)' : 'var(--text-secondary)' }}>
                    {p.discount_applied > 0 ? `-${formatAmount(p.discount_applied)}` : '—'}
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>{formatAmount(p.net_ssp)}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                    {p.vc_proportional_share !== 0 ? formatAmount(p.vc_proportional_share) : '—'}
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: p.vc_specific !== 0 ? 'var(--color-accent)' : 'var(--text-secondary)' }}>
                    {p.vc_specific !== 0 ? formatAmount(p.vc_specific) : '—'}
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {formatAmount(p.new_allocated)}
                  </td>
                </tr>
              );
            })}
            <tr style={{ borderTop: '2px solid var(--border-strong)' }}>
              <td colSpan={7} style={{ padding: '8px 4px', textAlign: 'right', fontSize: 11, color: 'var(--text-secondary)' }}>
                Σ (must equal total_value + all VC):
              </td>
              <td style={{
                padding: '8px 4px', textAlign: 'right', fontWeight: 700,
                color: invariantOk ? 'var(--text-primary)' : 'var(--color-negative)',
              }}>
                {formatAmount(sumCheck)}
                {!invariantOk && <span style={{ fontSize: 10, marginLeft: 4 }}>(≠ {formatAmount(expected)})</span>}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
